import type { Department } from "../../../contexts/AuthProvider";
import { DailyPrintoutGroupingOption } from "../../../utils/models";
import { getJobDisplayName } from "../../../utils/roles";
import { inverseRolesMap } from "../../../utils/utilFunctions";
import { customTimeGroupKeyLabel, isCustomTimeGroupKey } from "../layout/customTimeGroups";
import type { CustomColumnNamesConfig, DailyPrintoutGrouping } from "../types";

/**
 * Display label for one grouping-key segment (mapping §6.2 / §6.4 composite labels).
 *
 * Format-specific overrides:
 * - `NONE` → "All Staff"
 * - `ROLE` → resolves jobId → job display name (JobManagement on) or role short code (off)
 * - `START_TIME` → reformat `"HH:MM-HH:MM"` into the short `"7a-7p"` style for headers; a custom
 *   shift time group key reads as the group's heading from `customTimeGroupLabels`
 * - Others → identity
 *
 * When `censusByLocationName` has the location, a `LOCATION` segment gets ` - <census>` appended
 * (the "show location census" feature). Other grouping kinds never receive census.
 */
export const groupingKeyToDisplayName = (
	groupingKey: string,
	groupingOption: DailyPrintoutGroupingOption,
	jobManagementEnabled: boolean,
	department?: Department,
	censusByLocationName?: Map<string, number>,
	customTimeGroupLabels?: ReadonlyMap<string, string> | null,
): string => {
	if (groupingOption === DailyPrintoutGroupingOption.NONE) return "All Staff";
	if (groupingOption === DailyPrintoutGroupingOption.LOCATION) {
		const census = censusByLocationName?.get(groupingKey);
		return census !== undefined ? `${groupingKey} - ${census}` : groupingKey;
	}
	if (groupingOption === DailyPrintoutGroupingOption.ROLE) {
		if (jobManagementEnabled && department) {
			if (groupingKey === "No Job") return groupingKey;
			return getJobDisplayName(groupingKey, department) || groupingKey;
		}
		return inverseRolesMap[groupingKey as keyof typeof inverseRolesMap] || groupingKey;
	}
	if (groupingOption === DailyPrintoutGroupingOption.START_TIME) {
		if (isCustomTimeGroupKey(groupingKey)) {
			if (!customTimeGroupLabels) throw new Error(`Custom time group key without labels: ${groupingKey}`);
			return customTimeGroupKeyLabel(groupingKey, customTimeGroupLabels);
		}
		const [startTime, endTime] = groupingKey.split("-");
		const [startHourStr, startMinuteStr] = startTime.split(":");
		const [endHourStr, endMinuteStr] = endTime.split(":");
		const startHour = Number.parseInt(startHourStr, 10);
		const startMinute = Number.parseInt(startMinuteStr, 10);
		const endHour = Number.parseInt(endHourStr, 10);
		const endMinute = Number.parseInt(endMinuteStr, 10);

		const formatHM = (hour: number, minute: number): string => {
			const ampm = hour >= 12 ? "p" : "a";
			const displayHour = hour % 12 === 0 ? 12 : hour % 12;
			return minute === 0 ? `${displayHour}${ampm}` : `${displayHour}:${minute.toString().padStart(2, "0")}${ampm}`;
		};

		return `${formatHM(startHour, startMinute)}-${formatHM(endHour, endMinute)}`;
	}
	return groupingKey;
};

/**
 * Display label for a composite (multi-level) grouping key, joined with `" - "`.
 *
 * Composite keys are `" | "`-separated and produced by `group/sectionGrouping.ts` (mapping §6.4).
 * Each segment is mapped through `groupingKeyToDisplayName` for its matching active grouping kind.
 */
export const compositeGroupingKeyToDisplayName = (
	compositeKey: string,
	grouping: DailyPrintoutGrouping,
	jobManagementEnabled: boolean,
	department?: Department,
	censusByLocationName?: Map<string, number>,
	customTimeGroupLabels?: ReadonlyMap<string, string> | null,
): string => {
	const activeGroupings = grouping.filter((g) => g !== DailyPrintoutGroupingOption.NONE);

	if (compositeKey.includes(" | ")) {
		const keys = compositeKey.split(" | ");
		return keys
			.map((key, i) =>
				groupingKeyToDisplayName(
					key,
					activeGroupings[i] ?? DailyPrintoutGroupingOption.NONE,
					jobManagementEnabled,
					department,
					censusByLocationName,
					customTimeGroupLabels,
				),
			)
			.join(" - ");
	}
	const firstGrouping = activeGroupings[0] ?? DailyPrintoutGroupingOption.NONE;
	return groupingKeyToDisplayName(compositeKey, firstGrouping, jobManagementEnabled, department, censusByLocationName, customTimeGroupLabels);
};

/**
 * Resolve the human-readable header text for a column kind.
 *
 * Under JobManagement, the `role` column relabels to "Job" (mapping §5.2). All other columns
 * use their default English label from `COLUMN_TYPE_TO_HEADER`.
 */
import { COLUMN_TYPE_TO_HEADER } from "../constants";
import type { PrintoutColumnType } from "../types";
export const resolveColumnHeaderText = (type: PrintoutColumnType, jobManagementEnabled: boolean, customColumnNames: CustomColumnNamesConfig): string => {
	const original = type === "role" && jobManagementEnabled ? "Job" : COLUMN_TYPE_TO_HEADER[type];
	if (!customColumnNames.enabled) return original;
	const custom = customColumnNames.labels[type]?.trim();
	return custom ? custom : original;
};
